import { useState } from "react";
import { useTranslation } from "react-i18next";
import { 
  CheckCircle2, 
  XCircle, 
  Clock, 
  User,
  Building2,
  Calendar,
  History,
  Loader2,
  RefreshCw,
  FileText,
  Eye,
  ChevronLeft,
  ChevronRight,
  Radio,
  CreditCard,
  Shield,
  Copy,
  Send,
  Mail,
  MessageSquare,
  Phone as PhoneIcon,
  ChevronDown,
  Users,
  ClipboardCheck,
  CheckCheck,
  MapPin,
  Download
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { trpc } from "@/lib/trpc";
import { format } from "date-fns";
import { FormDataDisplay } from "@/modules/requests/FormDataDisplay";
import { toast } from "sonner";
import {
  FioriPageHeader,
  FioriFilterBar,
} from "@/components/fiori";

// typeLabels moved to inside component to use t()

const typeColors: Record<string, string> = {
  admin_visit: "bg-[#E8DCF5] text-[#5B2C93] border-[#5B2C93]",
  work_permit: "bg-[#E8DCF5] text-[#5B2C93] border-[#5B2C93]",
  material_entry: "bg-[#FEF3C7] text-[#D97706] border-[#D97706]",
  tep: "bg-[#E8DCF5] text-[#5B2C93] border-[#5B2C93]/20",
  mop: "bg-[#FFE5E5] text-[#FF6B6B] border-[#FF6B6B]",
  escort: "bg-[#D1FAE5] text-[#059669] border-[#059669]",
};

// Dynamic stage badge colors
const stageColors = [
  "bg-[#FEF3C7] text-[#D97706] border-[#D97706]",
  "bg-[#E8DCF5] text-[#5B2C93] border-[#5B2C93]",
  "bg-[#E8DCF5] text-[#5B2C93] border-[#5B2C93]",
  "bg-[#FFE5E5] text-[#FF6B6B] border-[#FF6B6B]",
  "bg-[#D1FAE5] text-[#059669] border-[#059669]",
];

const entryMethodConfig: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  qr_code: { label: "QR Code", icon: <Shield className="h-4 w-4" />, color: "text-[#059669] bg-[#D1FAE5]" },
  rfid: { label: "RFID Tag", icon: <Radio className="h-4 w-4" />, color: "text-[#5B2C93] bg-[#E8DCF5]" },
  card: { label: "Access Card", icon: <CreditCard className="h-4 w-4" />, color: "text-[#5B2C93] bg-[#E8DCF5]" },
};

function getStageColor(stageOrder: number): string {
  return stageColors[(stageOrder - 1) % stageColors.length];
}

export default function ApprovalHistory() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const typeLabels: Record<string, string> = {
    admin_visit: t('requests.types.adminVisit', 'Admin Visit'),
    work_permit: t('requests.types.workPermit', 'Work Permit'),
    material_entry: t('requests.types.materialEntry', 'Material Entry'),
    tep: t('requests.types.tep', 'TEP'),
    mop: t('requests.types.mop', 'MOP'),
    escort: t('requests.types.escort', 'Escort'),
  };
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [siteFilter, setSiteFilter] = useState<string>("all");
  const [page, setPage] = useState(0);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  const [resendingChannel, setResendingChannel] = useState<string | null>(null);
  const pageSize = 20;
  
  const { data: historyTasks, isLoading, isFetching, refetch } = trpc.requests.getMyApprovalHistory.useQuery({
    limit: pageSize,
    offset: page * pageSize,
  });
  
  // Calculate stats for KPI boxes
  const approvedCount = (historyTasks || []).filter((t: any) => t.taskStatus === 'approved').length;
  const rejectedCount = (historyTasks || []).filter((t: any) => t.taskStatus === 'rejected').length;
  
  // Get unique types and sites
  const uniqueTypes = Array.from(new Set((historyTasks || []).map((t: any) => t.request?.type).filter(Boolean))) as string[];
  const uniqueSites = Array.from(new Set((historyTasks || []).map((t: any) => t.request?.siteName).filter(Boolean))) as string[];
  
  // Filter by search, status, type, and site
  const filteredTasks = (historyTasks || []).filter((task: any) => {
    // Status filter
    if (statusFilter !== "all" && task.taskStatus !== statusFilter) return false;
    // Type filter
    if (typeFilter !== "all" && task.request?.type !== typeFilter) return false;
    // Site filter
    if (siteFilter !== "all" && task.request?.siteName !== siteFilter) return false;
    // Search filter
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      task.request?.requestNumber?.toLowerCase().includes(query) ||
      task.request?.visitorName?.toLowerCase().includes(query) ||
      task.request?.visitorCompany?.toLowerCase().includes(query) ||
      task.stageName?.toLowerCase().includes(query)
    );
  }).sort((a: any, b: any) => {
    const toMs = (v: any): number => {
      if (!v) return 0;
      const ms = typeof v === 'number' ? v : new Date(v).getTime();
      return isNaN(ms) ? 0 : ms;
    };
    const aTime = toMs(a.taskCompletedAt) || toMs(a.request?.createdAt);
    const bTime = toMs(b.taskCompletedAt) || toMs(b.request?.createdAt);
    return bTime - aTime;
  });
  
  const handleViewDetails = (task: any) => {
    setSelectedTask(task);
    setDetailsDialogOpen(true);
  };
  
  const handleCopyQrData = () => {
    if (selectedTask?.accessMethod?.qrCodeData) {
      navigator.clipboard.writeText(selectedTask.accessMethod.qrCodeData);
      toast.success(t('approvals.qrCopied', 'QR code data copied to clipboard'));
    }
  };
  
  const handleDownloadPdf = (requestId: number) => {
    // Open the PDF in a new tab for print/download
    const token = localStorage.getItem("app_session_token");
    const url = `/api/forms/pdf/${requestId}${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    window.open(url, '_blank');
  };
  
  const handleResendCredentials = async (channel: "email" | "sms" | "whatsapp") => {
    if (!selectedTask) return;
    
    setResendingChannel(channel);
    
    try {
      const visitorEmail = selectedTask.request?.visitorEmail;
      const visitorPhone = selectedTask.request?.visitorPhone;
      const accessMethod = selectedTask.accessMethod;
      
      if (channel === "email") {
        if (!visitorEmail) {
          toast.error(t('approvals.noEmailAvailable', 'No email address available'), { description: t('approvals.visitorEmailNotProvided', 'Visitor email is not provided') });
          setResendingChannel(null);
          return;
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
        toast.success(t('approvals.credentialsSentEmail', 'Access credentials sent via Email'), { 
          description: `${t('approvals.sentTo', 'Sent to')} ${visitorEmail}` 
        });
      } else if (channel === "sms") {
        if (!visitorPhone) {
          toast.error(t('approvals.noPhoneAvailable', 'No phone number available'), { description: t('approvals.visitorPhoneNotProvided', 'Visitor phone is not provided') });
          setResendingChannel(null);
          return;
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
        toast.success(t('approvals.credentialsSentSms', 'Access credentials sent via SMS'), { 
          description: `${t('approvals.sentTo', 'Sent to')} ${visitorPhone}` 
        });
      } else if (channel === "whatsapp") {
        if (!visitorPhone) {
          toast.error(t('approvals.noPhoneAvailable', 'No phone number available'), { description: t('approvals.visitorPhoneNotProvided', 'Visitor phone is not provided') });
          setResendingChannel(null);
          return;
        }
        const message = encodeURIComponent(
          `Your access credentials for ${selectedTask.request?.siteName || 'the facility'}:\n\n` +
          `Request: ${selectedTask.request?.requestNumber}\n` +
          `Valid: ${selectedTask.request?.startDate ? format(new Date(selectedTask.request.startDate), "MMM d, yyyy") : "N/A"} - ${selectedTask.request?.endDate ? format(new Date(selectedTask.request.endDate), "MMM d, yyyy") : "N/A"}\n` +
          (accessMethod?.entryMethod === "qr_code" ? `\nAccess Code: ${accessMethod.qrCodeData}` : "") +
          (accessMethod?.entryMethod === "rfid" ? `\nRFID Tag: ${accessMethod.rfidTag}` : "") +
          (accessMethod?.entryMethod === "card" ? `\nCard Number: ${accessMethod.cardNumber}` : "")
        );
        const phone = visitorPhone.replace(/[^0-9]/g, "");
        window.open(`https://wa.me/${phone}?text=${message}`, "_blank");
        toast.success(t('approvals.whatsappOpened', 'WhatsApp opened'), { description: t('approvals.messagePrepared', 'Message prepared for sending') });
      }
    } catch (error) {
      toast.error(t('approvals.failedToSend', 'Failed to send credentials'), { description: t('common.tryAgain', 'Please try again') });
    } finally {
      setResendingChannel(null);
    }
  };

  const activeFilters = [
    ...(statusFilter !== "all" ? [{ key: "status", label: `${t('approvals.statusLabel','Status')}: ${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}`, onRemove: () => setStatusFilter("all") }] : []),
    ...(typeFilter !== "all" ? [{ key: "type", label: `${t('approvals.typeLabel','Type')}: ${typeLabels[typeFilter] || typeFilter}`, onRemove: () => setSiteFilter("all") }] : []),
    ...(siteFilter !== "all" ? [{ key: "site", label: `${t('approvals.siteLabel','Site')}: ${siteFilter}`, onRemove: () => setSiteFilter("all") }] : []),
  ];

  // Stage progress component
  const StageProgress = ({ currentStage, totalStages }: { currentStage: number; totalStages: number }) => (
    <div className="flex items-center gap-1">
      {Array.from({ length: totalStages }).map((_, i) => (
        <div
          key={i}
          className={`w-2 h-2 rounded-full ${i < currentStage ? "bg-[#059669]" : i === currentStage - 1 ? "bg-[#D97706]" : "bg-[#E0E0E0]"}`}
        />
      ))}
      <span className="text-xs text-[#6B6B6B] ml-1">{currentStage}/{totalStages}</span>
    </div>
  );

  return (
    <div className="-mx-4 md:-mx-6 -mb-4 md:-mb-6 min-h-[calc(100vh-4rem)] bg-white px-4 md:px-6">
      <FioriPageHeader
        title={t("approvals.history", "Approval History")}
        subtitle={t("approvals.historyDescription", "View your past approval decisions and actions")}
        icon={<History className="h-5 w-5" />}
        count={filteredTasks.length}
        onRefresh={() => refetch()}
        isRefreshing={isFetching}
      />

      {/* KPI Summary Boxes */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 px-0 py-4">
        <div
          className={`bg-white border rounded-lg px-4 py-3 cursor-pointer transition-all ${
            statusFilter === "all" ? "border-[#5B2C93] ring-1 ring-[#5B2C93]/20 shadow-sm" : "border-[#E0E0E0] hover:border-[#5B2C93]/40"
          }`}
          onClick={() => setStatusFilter("all")}
        >
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-[#E8DCF5] flex items-center justify-center">
              <ClipboardCheck className="h-4 w-4 text-[#5B2C93]" />
            </div>
            <div>
              <p className="text-xs text-[#6B6B6B] font-medium">{t('approvals.totalDecisions', 'Total Decisions')}</p>
              <p className="text-xl font-semibold text-[#2C2C2C]">{historyTasks?.length || 0}</p>
            </div>
          </div>
        </div>
        <div
          className={`bg-white border rounded-lg px-4 py-3 cursor-pointer transition-all ${
            statusFilter === "approved" ? "border-[#5B2C93] ring-1 ring-[#5B2C93]/20 shadow-sm" : "border-[#E0E0E0] hover:border-[#5B2C93]/40"
          }`}
          onClick={() => setStatusFilter(statusFilter === "approved" ? "all" : "approved")}
        >
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-[#D1FAE5] flex items-center justify-center">
              <CheckCircle2 className="h-4 w-4 text-[#059669]" />
            </div>
            <div>
              <p className="text-xs text-[#6B6B6B] font-medium">{t('approvals.approved', 'Approved')}</p>
              <p className="text-xl font-semibold text-[#2C2C2C]">{approvedCount}</p>
            </div>
          </div>
        </div>
        <div
          className={`bg-white border rounded-lg px-4 py-3 cursor-pointer transition-all ${
            statusFilter === "rejected" ? "border-[#5B2C93] ring-1 ring-[#5B2C93]/20 shadow-sm" : "border-[#E0E0E0] hover:border-[#5B2C93]/40"
          }`}
          onClick={() => setStatusFilter(statusFilter === "rejected" ? "all" : "rejected")}
        >
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-[#FFE5E5] flex items-center justify-center">
              <XCircle className="h-4 w-4 text-[#FF6B6B]" />
            </div>
            <div>
              <p className="text-xs text-[#6B6B6B] font-medium">{t('approvals.rejected', 'Rejected')}</p>
              <p className="text-xl font-semibold text-[#2C2C2C]">{rejectedCount}</p>
            </div>
          </div>
        </div>
      </div>

      <FioriFilterBar
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={t("approvals.searchPlaceholder", "Search by request number, visitor, or company...")}
        activeFilters={activeFilters}
        onClearAll={() => { setStatusFilter("all"); setTypeFilter("all"); setSiteFilter("all"); }}
        filters={
          <>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[160px] h-9 text-sm bg-[#F5F5F5] border-[#E0E0E0]">
                <SelectValue placeholder={t("approvals.filterByStatus", "All Decisions")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("approvals.allDecisions", "All Decisions")}</SelectItem>
                <SelectItem value="approved">{t("approvals.approved", "Approved")}</SelectItem>
                <SelectItem value="rejected">{t("approvals.rejected", "Rejected")}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-[160px] h-9 text-sm bg-[#F5F5F5] border-[#E0E0E0]">
                <SelectValue placeholder={t("approvals.filterByType", "All Types")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("approvals.allTypes", "All Types")}</SelectItem>
                {uniqueTypes.filter((type) => type).map((type) => (
                  <SelectItem key={type} value={type}>{typeLabels[type] || type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={siteFilter} onValueChange={setSiteFilter}>
              <SelectTrigger className="w-full sm:w-[160px] h-9 text-sm bg-[#F5F5F5] border-[#E0E0E0]">
                <SelectValue placeholder={t("approvals.filterBySite", "All Sites")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("approvals.allSites", "All Sites")}</SelectItem>
                {uniqueSites.filter((site) => site).map((site) => (
                  <SelectItem key={site} value={site}>{site}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        }
      />

      {/* Task List - SAP Fiori Table Style */}
      <div className="mt-4">
        {isLoading ? (
          <div className="bg-white border border-[#E0E0E0] rounded-lg flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-[#5B2C93]" />
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="bg-white border border-[#E0E0E0] rounded-lg flex flex-col items-center justify-center py-16">
            <div className="w-12 h-12 rounded-full bg-[#F5F5F5] flex items-center justify-center mb-3">
              <History className="h-6 w-6 text-[#9CA3AF]" />
            </div>
            <h3 className="text-sm font-medium text-[#2C2C2C]">{t("approvals.noHistory", "No approval history")}</h3>
            <p className="text-xs text-[#6B6B6B] mt-1">{t("approvals.noHistoryDescription", "Your approval decisions will appear here once you start reviewing requests.")}</p>
          </div>
        ) : (
          <div className="bg-white border border-[#E0E0E0] rounded-lg overflow-hidden">
            {/* Table Header - hidden on mobile */}
            <div className="hidden lg:grid grid-cols-[1fr_100px_90px_120px_130px_100px_80px_80px] gap-2 px-4 py-2.5 bg-[#FAFAFA] border-b border-[#E0E0E0] text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">
              <span>{t('approvals.columns.request', 'Request')}</span>
              <span>{t('approvals.columns.type', 'Type')}</span>
              <span>{t('approvals.columns.stage', 'Stage')}</span>
              <span>{t('approvals.columns.visitor', 'Visitor')}</span>
              <span>{t('approvals.columns.approvers', 'Approver(s)')}</span>
              <span>{t('approvals.columns.site', 'Site')}</span>
              <span>{t('approvals.columns.decided', 'Decided')}</span>
              <span className="text-right">{t('approvals.columns.action', 'Action')}</span>
            </div>
            {/* Table Rows */}
            {filteredTasks.map((task: any) => (
              <div
                key={task.taskId}
                className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA] cursor-pointer"
                onClick={() => handleViewDetails(task)}
              >
                {/* Mobile card layout */}
                <div className="lg:hidden px-4 py-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <span className="font-mono text-sm font-medium text-[#5B2C93]">{task.request?.requestNumber}</span>
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${typeColors[task.request?.type] || "bg-[#F5F5F5] text-[#6B6B6B]"}`}>
                        {typeLabels[task.request?.type] || task.request?.type}
                      </span>
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${getStageColor(task.stageOrder || 1)}`}>
                        {task.stageName || `S${task.stageOrder}`}
                      </span>
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${task.taskStatus === 'approved' ? 'bg-[#D1FAE5] text-[#059669]' : 'bg-[#FFE5E5] text-[#FF6B6B]'}`}>
                        {task.taskStatus === 'approved' ? t('approvals.approved', 'Approved') : t('approvals.rejected', 'Rejected')}
                      </span>
                    </div>
                    <StageProgress currentStage={task.stageOrder || 1} totalStages={task.totalStages || 1} />
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-3.5 w-3.5 text-[#6B6B6B] flex-shrink-0" />
                    <span className="text-[#2C2C2C] font-medium truncate">{task.request?.visitorName || "N/A"}</span>
                    {task.request?.visitorCompany && <span className="text-[#6B6B6B] truncate">· {task.request.visitorCompany}</span>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#6B6B6B]">
                    <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{task.request?.siteName || "—"}</span>
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{task.taskCompletedAt ? format(new Date(task.taskCompletedAt), "MMM d") : "—"}</span>
                  </div>
                  <div className="flex items-center justify-end gap-1.5 pt-1" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-[#B0B0B0] hover:text-[#5B2C93] hover:bg-[#E8DCF5]"
                      onClick={(e) => { e.stopPropagation(); handleViewDetails(task); }}>
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                {/* Desktop table row */}
                <div className="hidden lg:grid grid-cols-[1fr_100px_90px_120px_130px_100px_80px_80px] gap-2 px-4 py-3 items-center">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-medium text-[#5B2C93]">{task.request?.requestNumber}</span>
                    <StageProgress currentStage={task.stageOrder || 1} totalStages={task.totalStages || 1} />
                  </div>
                  <div>
                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${typeColors[task.request?.type] || "bg-[#F5F5F5] text-[#6B6B6B]"}`}>
                      {typeLabels[task.request?.type] || task.request?.type}
                    </span>
                  </div>
                  <div>
                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${getStageColor(task.stageOrder || 1)}`}>
                      {task.stageName || `S${task.stageOrder}`}
                    </span>
                  </div>
                  <div className="truncate">
                    <span className="text-sm text-[#2C2C2C]">{task.request?.visitorName || "N/A"}</span>
                    {task.request?.visitorCompany && (
                      <p className="text-xs text-[#6B6B6B] truncate">{task.request.visitorCompany}</p>
                    )}
                  </div>
                  {/* Approver(s) column */}
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    {task.approvers && task.approvers.length > 0 ? (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className="flex items-center gap-1 text-xs text-[#5B2C93] hover:underline cursor-pointer max-w-full">
                            <Users className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{task.approvers[0]?.name || task.approvers[0]?.email}</span>
                            {task.approvers.length > 1 && (
                              <span className="flex-shrink-0 text-[10px] bg-[#E8DCF5] text-[#5B2C93] px-1 py-0.5 rounded-full font-medium">+{task.approvers.length - 1}</span>
                            )}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 p-0" align="start">
                          <div className="px-3 py-2 bg-[#F5F5F5] border-b border-[#E0E0E0]">
                            <h4 className="text-xs font-medium text-[#6B6B6B] uppercase tracking-wide flex items-center gap-1.5">
                              <Users className="h-3 w-3 text-[#5B2C93]" /> {t('approvals.approvers', 'Approvers')} ({task.approvers.length})
                            </h4>
                          </div>
                          <div className="divide-y divide-[#F0F0F0] max-h-[200px] overflow-y-auto">
                            {task.approvers.map((approver: any) => (
                              <div key={approver.userId} className="px-3 py-2 flex items-center gap-2">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-medium text-white ${
                                  approver.status === 'approved' ? 'bg-[#059669]' : approver.status === 'rejected' ? 'bg-[#FF6B6B]' : 'bg-[#5B2C93]'
                                }`}>
                                  {(approver.name || approver.email || '?').charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-[#2C2C2C] truncate">{approver.name || approver.email}</p>
                                  <p className="text-[10px] text-[#6B6B6B]">{approver.jobTitle || approver.assignedVia}</p>
                                </div>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                  approver.status === 'approved' ? 'bg-[#D1FAE5] text-[#059669]' : approver.status === 'rejected' ? 'bg-[#FFE5E5] text-[#FF6B6B]' : 'bg-[#FEF3C7] text-[#D97706]'
                                }`}>
                                  {approver.status}
                                </span>
                              </div>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                    ) : (
                      <span className="text-xs text-[#6B6B6B]">—</span>
                    )}
                  </div>
                  <span className="text-xs text-[#6B6B6B] truncate">{task.request?.siteName || "—"}</span>
                  <span className="text-xs text-[#6B6B6B]">
                    {task.taskCompletedAt ? format(new Date(task.taskCompletedAt), "MMM d") : "—"}
                  </span>
                  <div className="flex items-center justify-end gap-1.5">
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-[#B0B0B0] hover:text-[#5B2C93] hover:bg-[#E8DCF5]"
                      onClick={(e) => { e.stopPropagation(); handleViewDetails(task); }}>
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>{/* end desktop grid */}
              </div>
            ))}
            {/* Footer */}
            <div className="px-4 py-2 bg-[#FAFAFA] border-t border-[#E0E0E0] text-xs text-[#6B6B6B]">
              {t('approvals.showingDecisions', 'Showing {{count}} of {{total}} decisions', { count: filteredTasks.length, total: historyTasks?.length || 0 })}
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {(historyTasks || []).length > 0 && (
        <div className="flex items-center justify-between border-t pt-4">
          <p className="text-sm text-[#6B6B6B]">
            {t("approvals.showingPage", "Page")} {page + 1}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              {t("common.previous", "Previous")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={(historyTasks || []).length < pageSize}
            >
              {t("common.next", "Next")}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#5B2C93]" />
              {t("approvals.decisionDetails", "Decision Details")}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTask && (
            <div className="space-y-4">
              {/* Header info */}
              <div className="flex items-center justify-between p-3 bg-[#F5F5F5] rounded-lg">
                <div>
                  <span className="font-mono text-lg font-medium">{selectedTask.request?.requestNumber}</span>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className={typeColors[selectedTask.request?.type] || "bg-[#F5F5F5]"}>
                      {typeLabels[selectedTask.request?.type] || selectedTask.request?.type}
                    </Badge>
                    <Badge variant="outline" className={getStageColor(selectedTask.stageOrder || 1)}>
                      {selectedTask.stageName || `Stage ${selectedTask.stageOrder}`}
                    </Badge>
                    <Badge 
                      variant="outline" 
                      className={selectedTask.taskStatus === 'approved' 
                        ? 'bg-[#D1FAE5] text-[#059669] border-[#059669]' 
                        : 'bg-[#FFE5E5] text-[#FF6B6B] border-[#FF6B6B]'
                      }
                    >
                      {selectedTask.taskStatus === 'approved' ? (
                        <><CheckCircle2 className="h-3 w-3 mr-1" /> {t("approvals.approved", "Approved")}</>
                      ) : (
                        <><XCircle className="h-3 w-3 mr-1" /> {t("approvals.rejected", "Rejected")}</>
                      )}
                    </Badge>
                    {/* Overall request status */}
                    {selectedTask.request?.status && (
                      <Badge variant="outline" className={`text-xs ${
                        selectedTask.request.status === 'approved' ? 'bg-[#D1FAE5] text-[#059669] border-[#059669]' :
                        selectedTask.request.status === 'rejected' ? 'bg-[#FFE5E5] text-[#FF6B6B] border-[#FF6B6B]' :
                        'bg-[#FEF3C7] text-[#D97706] border-[#D97706]'
                      }`}>
                        {t('approvals.requestLabel','Request')}: {selectedTask.request.status.replace(/_/g, ' ')}
                      </Badge>
                    )}
                  </div>
                </div>
                <StageProgress 
                  currentStage={selectedTask.stageOrder || 1} 
                  totalStages={selectedTask.totalStages || 1}
                />
              </div>
              
              {/* Access Method - Compact, no QR image */}
              {selectedTask.taskStatus === 'approved' && selectedTask.accessMethod?.entryMethod && (
                <div className="bg-[#D1FAE5] border border-[#059669] rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Shield className="h-4 w-4 text-[#059669]" />
                      <Badge variant="secondary" className={`${entryMethodConfig[selectedTask.accessMethod.entryMethod]?.color} gap-2 text-sm px-3 py-1`}>
                        {entryMethodConfig[selectedTask.accessMethod.entryMethod]?.icon}
                        {entryMethodConfig[selectedTask.accessMethod.entryMethod]?.label}
                      </Badge>
                      {selectedTask.accessMethod.qrCodeData && (
                        <div className="flex items-center gap-2">
                          <code className="text-xs font-mono bg-white px-2 py-1 rounded border">{selectedTask.accessMethod.qrCodeData}</code>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={handleCopyQrData}>
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                      {selectedTask.accessMethod.rfidTag && (
                        <code className="text-xs font-mono bg-white px-2 py-1 rounded border">{selectedTask.accessMethod.rfidTag}</code>
                      )}
                      {selectedTask.accessMethod.cardNumber && (
                        <code className="text-xs font-mono bg-white px-2 py-1 rounded border">{selectedTask.accessMethod.cardNumber}</code>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedTask.accessMethod.accessGrantedByName && (
                        <span className="text-xs text-[#6B6B6B]">by {selectedTask.accessMethod.accessGrantedByName}</span>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="gap-1 h-7 text-xs" disabled={!!resendingChannel}>
                            {resendingChannel ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                            {t('approvals.resend', 'Resend')}
                            <ChevronDown className="h-3 w-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleResendCredentials("email")} className="gap-2">
                            <Mail className="h-4 w-4" /> Email
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleResendCredentials("sms")} className="gap-2">
                            <MessageSquare className="h-4 w-4" /> SMS
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleResendCredentials("whatsapp")} className="gap-2">
                            <PhoneIcon className="h-4 w-4" /> WhatsApp
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              )}
              
              {/* 3-Column Details Grid */}
              <div className="grid grid-cols-3 gap-6">
                {/* Column 1: Visitor Information */}
                <div className="border-l-4 border-[#5B2C93] bg-[#F5F5F5] rounded-r-lg p-4 space-y-4">
                  <h4 className="text-sm font-medium text-[#2C2C2C] flex items-center gap-2">
                    <User className="h-4 w-4 text-[#5B2C93]" />
                    {t('approvals.visitorInformation', 'Visitor Information')}
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-[#2C2C2C]">{t('approvals.fullName', 'Full Name')}</label>
                      <p className="text-sm font-medium">{selectedTask.request?.visitorName || "N/A"}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#2C2C2C]">{t('approvals.company', 'Company')}</label>
                      <p className="text-sm font-medium">{selectedTask.request?.visitorCompany || "N/A"}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#2C2C2C]">{t('approvals.idType', 'ID Type')}</label>
                      <p className="text-sm font-medium">{selectedTask.request?.visitorIdType?.toUpperCase().replace(/_/g, ' ') || "N/A"}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#2C2C2C]">{t('approvals.idNumber', 'ID Number')}</label>
                      <p className="text-sm font-medium">{selectedTask.request?.visitorIdNumber || "N/A"}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#2C2C2C]">{t('approvals.phone', 'Phone')}</label>
                      <p className="text-sm font-medium">{selectedTask.request?.visitorPhone || "N/A"}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#2C2C2C]">{t('approvals.email', 'Email')}</label>
                      <p className="text-sm font-medium truncate">{selectedTask.request?.visitorEmail || "N/A"}</p>
                    </div>
                  </div>
                </div>
                
                {/* Column 2: Visit Details */}
                <div className="border-l-4 border-[#5B2C93] bg-[#F5F5F5] rounded-r-lg p-4 space-y-4">
                  <h4 className="text-sm font-medium text-[#2C2C2C] flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-[#5B2C93]" />
                    {t('approvals.visitDetails', 'Visit Details')}
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-[#2C2C2C]">{t('approvals.purpose', 'Purpose')}</label>
                      <p className="text-sm font-medium">{selectedTask.request?.purpose || "N/A"}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#2C2C2C]">{t('approvals.startDate', 'Start Date')}</label>
                      <p className="text-sm font-medium">
                        {selectedTask.request?.startDate ? format(new Date(selectedTask.request.startDate), "EEEE, MMM d, yyyy") : "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#2C2C2C]">{t('approvals.endDate', 'End Date')}</label>
                      <p className="text-sm font-medium">
                        {selectedTask.request?.endDate ? format(new Date(selectedTask.request.endDate), "EEEE, MMM d, yyyy") : "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#2C2C2C]">{t('approvals.visitTime', 'Visit Time')}</label>
                      <p className="text-sm font-medium">
                        {selectedTask.request?.startTime || "00:00"} - {selectedTask.request?.endTime || "23:59"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#2C2C2C]">{t('approvals.submittedOn', 'Submitted On')}</label>
                      <p className="text-sm font-medium">
                        {selectedTask.request?.createdAt ? format(new Date(selectedTask.request.createdAt), "MMM d, yyyy 'at' HH:mm") : "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Column 3: Location & Workflow */}
                <div className="border-l-4 border-[#059669] bg-[#F5F5F5] rounded-r-lg p-4 space-y-4">
                  <h4 className="text-sm font-medium text-[#2C2C2C] flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-[#059669]" />
                    {t('approvals.locationWorkflow', 'Location & Workflow')}
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-[#2C2C2C]">{t('approvals.site', 'Site')}</label>
                      <p className="text-sm font-medium">{selectedTask.request?.siteName || "N/A"}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#2C2C2C]">{t('approvals.workflow', 'Workflow')}</label>
                      <p className="text-sm font-medium">{selectedTask.workflowName || "Default"}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#2C2C2C]">{t('approvals.yourStage', 'Your Stage')}</label>
                      <p className="text-sm font-medium">{selectedTask.stageName || "N/A"}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#2C2C2C]">{t('approvals.assignedAt', 'Assigned At')}</label>
                      <p className="text-sm font-medium">
                        {selectedTask.taskCreatedAt ? format(new Date(selectedTask.taskCreatedAt), "MMM d, yyyy HH:mm") : "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#2C2C2C]">{t('approvals.decidedAt', 'Decided At')}</label>
                      <p className="text-sm font-medium">
                        {selectedTask.taskCompletedAt ? format(new Date(selectedTask.taskCompletedAt), "MMM d, yyyy HH:mm") : "N/A"}
                      </p>
                    </div>
                    {/* Approvers */}
                    <div>
                      <label className="text-sm font-medium text-[#2C2C2C] flex items-center gap-1">
                        <Users className="h-3.5 w-3.5 text-[#5B2C93]" /> {t('approvals.approverLabel', 'Approver(s)')}
                      </label>
                      {selectedTask.approvers && selectedTask.approvers.length > 0 ? (
                        <div className="mt-1 space-y-1.5">
                          {selectedTask.approvers.map((approver: any) => (
                            <div key={approver.userId} className="flex items-center gap-2">
                              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-medium text-white ${
                                approver.status === 'approved' ? 'bg-[#059669]' : approver.status === 'rejected' ? 'bg-[#FF6B6B]' : 'bg-[#5B2C93]'
                              }`}>
                                {(approver.name || approver.email || '?').charAt(0).toUpperCase()}
                              </div>
                              <span className="text-sm font-medium">{approver.name || approver.email}</span>
                              <span className={`text-[10px] px-1 py-0.5 rounded ${
                                approver.status === 'approved' ? 'bg-[#D1FAE5] text-[#059669]' : approver.status === 'rejected' ? 'bg-[#FFE5E5] text-[#FF6B6B]' : 'bg-[#FEF3C7] text-[#D97706]'
                              }`}>
                                {approver.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm font-medium">N/A</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Dynamic Form Data */}
              {selectedTask.request?.formData && selectedTask.request?.selectedTypeIds && (
                <FormDataDisplay
                  formData={selectedTask.request.formData as Record<string, any>}
                  selectedTypeIds={selectedTask.request.selectedTypeIds as number[]}
                  categoryId={selectedTask.request.categoryId}
                  compact={false}
                />
              )}
              
              {/* Approval Timeline */}
              <div className="border-l-4 border-[#D97706] bg-[#F5F5F5] rounded-r-lg p-4">
                  <h4 className="text-sm font-medium text-[#2C2C2C] mb-3 flex items-center gap-2">
                  <History className="h-4 w-4 text-[#D97706]" />
                  {t('approvals.approvalTimeline', 'Approval Timeline')}
                </h4>
                
                {/* Stage Progress Indicator */}
                <div className="flex items-center gap-2 mb-4">
                  {Array.from({ length: selectedTask.totalStages || 1 }).map((_: unknown, idx: number) => (
                    <div key={idx} className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                        idx + 1 < (selectedTask.stageOrder || 1) 
                          ? "bg-[#D1FAE5] text-[#059669] border-2 border-[#059669]" 
                          : idx + 1 === (selectedTask.stageOrder || 1)
                            ? selectedTask.taskStatus === 'approved' ? "bg-[#D1FAE5] text-[#059669] border-2 border-[#059669]" : "bg-[#FFE5E5] text-[#FF6B6B] border-2 border-[#FF6B6B]"
                            : "bg-[#F5F5F5] text-[#6B6B6B] border-2 border-[#E0E0E0]"
                      }`}>
                        {idx + 1 < (selectedTask.stageOrder || 1) || (idx + 1 === (selectedTask.stageOrder || 1) && selectedTask.taskStatus === 'approved') ? (
                          <CheckCircle2 className="h-4 w-4" />
                        ) : idx + 1 === (selectedTask.stageOrder || 1) && selectedTask.taskStatus === 'rejected' ? (
                          <XCircle className="h-4 w-4" />
                        ) : (
                          idx + 1
                        )}
                      </div>
                      {idx < (selectedTask.totalStages || 1) - 1 && (
                        <div className={`w-8 h-0.5 ${
                          idx + 1 < (selectedTask.stageOrder || 1) ? "bg-[#D1FAE5]" : "bg-[#E0E0E0]"
                        }`} />
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Timeline Entries */}
                {selectedTask.approvalHistory && selectedTask.approvalHistory.length > 0 ? (
                  <div className="space-y-0 max-h-[350px] overflow-y-auto relative">
                    <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-[#E0E0E0]" />
                    
                    {selectedTask.approvalHistory.map((history: any, idx: number) => {
                      const actionBgColors: Record<string, string> = {
                        approved: "bg-[#D1FAE5]", rejected: "bg-[#FF6B6B]", decision_made: "bg-[#E8DCF5]",
                        info_requested: "bg-[#FEF3C7]", submitted: "bg-[#E8DCF5]", escalated: "bg-[#E8DCF5]",
                        workflow_started: "bg-[#F5F5F5]", workflow_completed: "bg-[#059669]",
                        stage_completed: "bg-[#D1FAE5]", task_assigned: "bg-[#5B2C93]",
                        sent_back: "bg-[#FEF3C7]", clarification_requested: "bg-[#FEF3C7]",
                        clarification_provided: "bg-[#5B2C93]", task_reassigned: "bg-[#E8DCF5]",
                      };
                      const actionColors: Record<string, string> = {
                        approved: "border-[#059669]", rejected: "border-[#FF6B6B]", decision_made: "border-[#5B2C93]",
                        info_requested: "border-[#D97706]", submitted: "border-[#5B2C93]", escalated: "border-[#5B2C93]",
                        workflow_started: "border-[#B0B0B0]", workflow_completed: "border-[#059669]",
                        stage_completed: "border-[#059669]", task_assigned: "border-[#5B2C93]",
                        sent_back: "border-[#D97706]", clarification_requested: "border-[#D97706]",
                        clarification_provided: "border-[#5B2C93]", task_reassigned: "border-[#5B2C93]",
                      };
                      const actionLabels: Record<string, string> = {
                        approved: t('approvals.timeline.approved', 'Approved'),
                        rejected: t('approvals.timeline.rejected', 'Rejected'),
                        decision_made: t('approvals.timeline.decisionMade', 'Decision Made'),
                        info_requested: t('approvals.timeline.clarificationRequested', 'Clarification Requested'),
                        submitted: t('approvals.timeline.submitted', 'Submitted'),
                        escalated: t('approvals.timeline.escalated', 'Escalated'),
                        workflow_started: t('approvals.timeline.workflowStarted', 'Workflow Started'),
                        workflow_completed: t('approvals.timeline.workflowCompleted', 'Workflow Completed'),
                        stage_completed: t('approvals.timeline.stageCompleted', 'Stage Completed'),
                        task_assigned: t('approvals.timeline.taskAssigned', 'Task Assigned'),
                        sent_back: t('approvals.timeline.sentBack', 'Sent Back'),
                        clarification_requested: t('approvals.timeline.clarificationRequested', 'Clarification Requested'),
                        clarification_provided: t('approvals.timeline.clarificationProvided', 'Clarification Provided'),
                        task_reassigned: t('approvals.timeline.taskReassigned', 'Task Reassigned'),
                      };
                      
                      const details = (history.details as any) || {};
                      const stageName = details.stageName || details.workflowName || 
                        (history.actionType === "workflow_started" ? t('approvals.timeline.initiated', 'Initiated') :
                         history.actionType === "workflow_completed" ? (details.newStatus === "approved" ? t('approvals.timeline.finalApproval', 'Final Approval') : t('approvals.timeline.completed', 'Completed')) :
                         t('approvals.timeline.stage', 'Stage'));
                      const comments = details.comments || details.reason || details.response || "";
                      const decision = details.decision || details.newStatus || "";
                      const decisionLabel = history.actionType === "decision_made" 
                        ? (decision === "approved" ? t('approvals.approved','Approved') : decision === "rejected" ? t('approvals.rejected','Rejected') : decision.replace(/_/g, " "))
                        : "";
                      
                      return (
                        <div key={idx} className="relative pl-10 pb-4">
                          <div className={`absolute left-2 top-1 w-5 h-5 rounded-full flex items-center justify-center z-10 ${actionBgColors[history.actionType] || "bg-[#B0B0B0]"}`}>
                            {history.actionType === 'approved' || history.actionType === 'decision_made' ? <CheckCircle2 className="h-3 w-3 text-white" /> :
                             history.actionType === 'rejected' ? <XCircle className="h-3 w-3 text-white" /> :
                             <Clock className="h-3 w-3 text-white" />}
                          </div>
                          
                          <div className={`border-l-2 pl-3 py-1 ${actionColors[history.actionType] || "border-[#E0E0E0]"}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-sm">
                                  {actionLabels[history.actionType] || history.actionType.replace(/_/g, " ")}
                                </span>
                                <Badge variant="outline" className="text-xs px-1.5 py-0">{stageName}</Badge>
                                {decisionLabel && history.actionType === "decision_made" && (
                                  <Badge variant="outline" className={`text-xs px-1.5 py-0 ${
                                    decision === "approved" ? "bg-[#D1FAE5] text-[#059669] border-[#059669]" :
                                    decision === "rejected" ? "bg-[#FFE5E5] text-[#FF6B6B] border-[#FF6B6B]" :
                                    "bg-[#F5F5F5] text-[#2C2C2C]"
                                  }`}>{decisionLabel}</Badge>
                                )}
                              </div>
                              <span className="text-xs text-[#6B6B6B] whitespace-nowrap ml-2">
                                {history.actionAt ? format(new Date(history.actionAt), "MMM d, yyyy HH:mm") : ""}
                              </span>
                            </div>
                            <div className="text-xs text-[#6B6B6B] mt-0.5">
                              <span className="font-medium">{history.userName || history.userEmail || "System"}</span>
                            </div>
                            {comments && (
                              <div className="mt-1.5 p-2 bg-[#F5F5F5] rounded border border-[#E0E0E0] text-sm">
                                <MessageSquare className="h-3 w-3 inline-block mr-1 text-[#6B6B6B]" />
                                <span className="italic text-[#6B6B6B]">"{comments}"</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-[#6B6B6B] italic">{t('approvals.noHistoryForRequest', 'No approval history available for this request.')}</p>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>
              {t("common.close", "Close")}
            </Button>
            {selectedTask?.request?.id && selectedTask?.request?.status === 'approved' && (
              <Button 
                variant="outline" 
                onClick={() => handleDownloadPdf(selectedTask.request.id)} 
                className="gap-2 text-[#5B2C93] hover:text-[#5B2C93] hover:bg-[#E8DCF5]"
              >
                <Download className="h-4 w-4" />
                {t("approvals.downloadPdf", "Download Form PDF")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
